import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import './LegalApp.css';
import axios from "axios";
import { FaEye, FaEyeSlash } from "react-icons/fa";
//import bcrypt from "bcryptjs";

const Login = ({ API_URL }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const otpRef = useRef(null);

    const [email, setEmail] = useState(location.state ? location.state.email : '');
    const [password, setPassword] = useState('');
    const [isLoginLoading, setIsLoginLoading] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showOTP, setShowOTP] = useState(false);

    //const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const [isMobile, setIsMobile] = useState(false);
    const [warningMessage, setWarningMessage] = useState(null);

    // inside your component:
    const OTP_SECONDS = 90;

    const [otpSent, setOtpSent] = useState(false);
    const [timeLeft, setTimeLeft] = useState(OTP_SECONDS);

    // Optional: if you want separate loading for OTP (recommended)
    const [isOTPLoading, setIsOTPLoading] = useState(false);
    // Helper: normalize mobile similar to your React Native logic
    const normalizeMobile = (m) => {
        let e = (m || "").trim();
        if (e.startsWith("+91")) e = e.slice(3);
        if (e.startsWith("91")) e = e.slice(2);
        if (e.startsWith("0")) e = e.slice(1);
        return e;
    };

    // Normalize input like "+91 98765-43210" -> "9876543210"
    const normalizeIndianMobile = (raw) => {
        let s = String(raw || "").trim().replace(/\D/g, ""); // digits only

        if (s.startsWith("91") && s.length > 10) s = s.slice(2); // remove country code
        if (s.startsWith("0") && s.length > 10) s = s.slice(1);  // remove leading 0 (rare)
        if (s.length > 10) s = s.slice(-10); // keep last 10 digits if extra

        return s;
    };

    // Validate Indian mobile: 10 digits, first digit 6-9
    const isValidIndianMobile = (raw) => {
        const m = normalizeIndianMobile(raw);
        return /^[6-9]\d{9}$/.test(m);
    };


    // Format remaining time similar to mobile feel (mm ss)
    const formatTimeLeft = (secs) => {
        if (secs >= 60) {
            const mins = Math.floor(secs / 60);
            const rem = secs % 60;
            return `${mins}m ${String(rem).padStart(2, "0")}s`;
        }
        return `${secs}s`;
    };

    // Countdown timer
    useEffect(() => {
        let timer;
        if (otpSent && timeLeft > 0) {
            timer = setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
        } else if (otpSent && timeLeft <= 0) {
            setOtpSent(false);
            setTimeLeft(OTP_SECONDS);
        }
        return () => clearInterval(timer);
    }, [otpSent, timeLeft]);

    const sendOTP = async () => {
        if (!isValidIndianMobile(email)) {
            setErrorMessage("Please enter a valid Indian mobile number.");
            setShowError(true);
            return;
        }

        const mobile = normalizeIndianMobile(email);

        if (!mobile || mobile.length < 10) {
            setErrorMessage("Please enter a valid mobile number.");
            setShowError(true);
            return;
        }

        setPassword("");         // clear OTP field
        setOtpSent(true);        // start timer immediately for UI responsiveness
        setTimeLeft(OTP_SECONDS);

        // Auto-focus OTP field
        setTimeout(() => {
            otpRef.current?.focus();
        }, 0);

        await sendOTPCall(mobile);
    };

    const sendOTPCall = async (mobile) => {
        try {
            setIsOTPLoading(true);

            const res = await axios.post(`${API_URL}/user/otp`, { mobile });

            if ([200, 201, 204].includes(res.status)) {
                setOtpSent(true);
                setTimeLeft(OTP_SECONDS);
                return;
            }

            setErrorMessage(`Unexpected response: ${res.status}`);
            setShowError(true);
            setOtpSent(false);
        } catch (err) {
            const status = err?.response?.status;
            const data = err?.response?.data;

            const serverMsg =
                (typeof data === "string" && data) ||
                data?.message ||
                data?.error ||
                data?.details ||
                null;

            let msg = serverMsg || "Something went wrong. Please try again.";

            if (status === 400) msg = serverMsg || "Invalid mobile number.";
            else if (status === 409) msg = serverMsg || "OTP already sent. Please wait.";
            else if (status === 429) msg = serverMsg || "Too many requests. Try again later.";
            else if (status >= 500) msg = serverMsg || "Server error. Please try again later.";

            setErrorMessage(msg);
            setShowError(true);

            // If OTP failed, stop timer state
            setOtpSent(false);
        } finally {
            setIsOTPLoading(false);
        }
    };


    useEffect(() => {
        const checkMobile = () => {
            return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                navigator.userAgent
            );
        };

        setIsMobile(checkMobile());

        if (isMobile) {
            setWarningMessage('For the best experience, use this application on a Desktop or Laptop computer.');
        } else {
            setWarningMessage(null);
        }
    }, []);

    const handleEmailChange = (e) => {
        const digitsOnly = (e.target.value || "").replace(/\D/g, ""); // keep only 0-9
        setEmail(digitsOnly.slice(0, 10)); // max 10 digits
    };


    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
    };

    // Safe JWT payload decode (base64url + UTF-8)
    const parseJwtPayload = (token) => {
        const payloadPart = token?.split(".")?.[1];
        if (!payloadPart) return {};

        // base64url -> base64
        let base64 = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
        // pad to multiple of 4
        while (base64.length % 4) base64 += "=";

        // decode as UTF-8 safe string
        const jsonStr = decodeURIComponent(
            atob(base64)
                .split("")
                .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
                .join("")
        );

        return JSON.parse(jsonStr);
    };

    const handleLogin = async (e) => {
        e.preventDefault();

        setIsLoginLoading(true);
        localStorage.clear();

        // const isSpecial = email === "9582822225" || email === "9582822226";
        // const otpFlag = isSpecial && !otpSent ? false : true;

        fetch(API_URL + '/login', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            // body: JSON.stringify({ email, password: hashedPassword }),
            body: JSON.stringify({ email, password: password, otp: true }),
        })
            .then(response => {
                if (!response.ok) {
                    if (response.status === 401) {
                        setErrorMessage('Invalid Mobile or Password.');
                    } else if (response.status === 403) {
                        navigate('/reset', { state: { email, notify: true } });
                    }
                    else {
                        setErrorMessage('An error occurred. Please try after some time.');
                    }
                    setShowError(true);
                    setIsLoginLoading(false);
                    throw new Error('Login failed.');
                }
                return response.json()
            })
            .then(data => {
                if (data.message) {
                    //console.log('Logged in successfully:', data.message);
                    // var jwtAttributes = data.token.split('.')[1];
                    // var role = JSON.parse(atob(jwtAttributes)).role;
                    // var firstName = JSON.parse(atob(jwtAttributes)).firstName;
                    // var lastName = JSON.parse(atob(jwtAttributes)).lastName;
                    // var designation = JSON.parse(atob(jwtAttributes)).designation;
                    // var language = JSON.parse(atob(jwtAttributes)).language;

                    let payload = {};
                    try {
                        payload = parseJwtPayload(data.token);
                    } catch (e) {
                        console.log("JWT payload decode failed:", e);
                        setErrorMessage('An error occurred. Please try after some time.');
                        return;
                    }

                    const role = payload?.role ?? "";
                    const firstName = payload?.firstName ?? "";
                    const lastName = payload?.lastName ?? "";
                    const designation = payload?.designation ?? "";
                    const language = payload?.language ?? "";

                    localStorage.setItem('jwtToken', data.token);
                    localStorage.setItem('email', email);
                    localStorage.setItem('role', role);
                    localStorage.setItem("firstName", (firstName ?? "").toString().trim());
                    localStorage.setItem("lastName", (lastName ?? "").toString().trim());
                    localStorage.setItem("designation", (designation ?? "").toString().trim());
                    localStorage.setItem('language', language);

                    setIsLoginLoading(false);
                    navigate('/mycases');

                    // if (data.cases) {
                    //     navigate('/mycases');
                    // } else {
                    //     navigate('/upload');
                    // }
                } else {
                    setErrorMessage('An error occurred. Please try after some time.');
                    setShowError(true);
                    setIsLoginLoading(false);
                    throw new Error('Login failed.');
                }
            })
            .catch(error => {
                if (error.message === '') {
                    setErrorMessage('An error occurred. Please try after some time.');
                }
                setShowError(true);
                setIsLoginLoading(false);
            });
    };

    const handleCloseError = () => {
        setShowError(false);
        setErrorMessage('');
    };

    const handleCloseWarning = () => {
        setWarningMessage(null);
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Navbar />
            <main style={{
                flex: '1',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f9f9f9'
            }}>
                <div style={{
                    padding: '20px', /* Only the card has padding now */
                    width: '100%',
                    maxWidth: '400px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    borderRadius: '8px',
                    backgroundColor: 'white'
                }}>
                    <h1 style={{ textAlign: "center" }}>Login</h1>
                    <form onSubmit={handleLogin}>
                        <div style={{ marginBottom: "12px" }}>
                            <label htmlFor="email">Mobile</label>

                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <input
                                    id="email"
                                    value={email}
                                    placeholder='Enter Mobile Number'
                                    inputMode="numeric"
                                    pattern="\d*"
                                    maxLength={10}
                                    onChange={handleEmailChange}
                                    required
                                    className="form-control"
                                    style={{ flex: 1, margin: 0 }}
                                />

                                {/* Fixed width right column like mobile screen */}
                                <div
                                    style={{
                                        width: "110px",
                                        flex: "0 0 110px",     // do not shrink/grow in flex row
                                        boxSizing: "border-box",
                                    }}
                                >
                                    {!otpSent ? (
                                        <button
                                            type="button"
                                            onClick={sendOTP}
                                            disabled={isOTPLoading || isLoginLoading || !email || email.length < 10}
                                            style={{
                                                width: "100%",
                                                boxSizing: "border-box",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                height: "40px",     // fixed height to match timer box
                                                backgroundColor: "#509acb",
                                                color: "#fff",
                                                border: "1px solid #509acb", // keep border consistent
                                                borderRadius: "4px",
                                                padding: "0 12px",  // use horizontal padding only since height is fixed
                                                fontWeight: 600,
                                                cursor:
                                                    isOTPLoading || isLoginLoading || !email || email.length < 10
                                                        ? "not-allowed"
                                                        : "pointer",
                                                opacity:
                                                    isOTPLoading || isLoginLoading || !email || email.length < 10 ? 0.7 : 1,
                                            }}
                                        >
                                            {isOTPLoading ? <div className="spinner"></div> : "Send OTP"}
                                        </button>
                                    ) : (
                                        <div
                                            style={{
                                                width: "100%",
                                                boxSizing: "border-box",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                height: "40px",     // same fixed height
                                                border: "1px solid #ccc",
                                                borderRadius: "4px",
                                                padding: "0 12px",
                                                textAlign: "center",
                                                fontWeight: 600,
                                                color: "#094268",
                                                background: "#fff",
                                                overflow: "hidden", // prevents any text causing layout quirks
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {formatTimeLeft(timeLeft)}
                                        </div>
                                    )}
                                </div>

                            </div>

                            {/* {otpSent && (
                                <div style={{ marginTop: "6px", color: "#094268", fontSize: "14px" }}>
                                    Resend OTP in {formatTimeLeft(timeLeft)}
                                </div>
                            )} */}
                        </div>


                        <br />
                        <div>
                            <label htmlFor="password">OTP</label>
                            <div style={{ position: "relative" }}>
                                <input
                                    type={showOTP ? "text" : "password"}
                                    id="password"
                                    ref={otpRef}
                                    value={password}
                                    placeholder='Enter OTP'
                                    onChange={handlePasswordChange}
                                    required
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && email && password) {
                                            handleLogin(e);
                                        }
                                    }}
                                    className="form-control"
                                    style={{ paddingRight: "40px" }}
                                />
                                <span
                                    onClick={() => setShowOTP(!showOTP)}
                                    style={{
                                        position: "absolute",
                                        right: "10px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        cursor: "pointer",
                                        color: "#666",
                                        display: "flex",
                                        alignItems: "center",
                                        zIndex: 10
                                    }}
                                >
                                    {showOTP ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                                </span>
                            </div>
                        </div>
                        <div className="popup-buttons" style={{ width: "100%" }}>
                            <button
                                type="submit"
                                className="submit-button"
                                style={{
                                    fontWeight: "bold",
                                    width: "100%",
                                    height: "45px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: 0
                                }}
                                disabled={isLoginLoading || !email || !password || email.length < 10}
                            >
                                {isLoginLoading ? (
                                    <div className="spinner" style={{ width: "20px", height: "20px", margin: 0, borderTopColor: "white" }}></div>
                                ) : (
                                    "Login"
                                )}
                            </button>
                        </div>

                        <br />
                        <p style={{ textAlign: "center" }}>
                            Don't have an account? Click <a href="/register" style={{ textDecoration: "underline", textUnderlineOffset: '3px' }}>here</a> to register.
                        </p>
                        {/* <p style={{ textAlign: "center" }}>
                            Forgot Password? Click <a href="/reset" style={{ textDecoration: "underline", textUnderlineOffset: '3px' }}>here</a> to reset.
                        </p> */}
                    </form>

                    {showError && errorMessage && (
                        <div className="popup-overlay">
                            <div className="popup-container">
                                <h3>Error</h3>
                                <p>{errorMessage}</p>
                                <button
                                    className="submit-popup-button"
                                    onClick={handleCloseError}
                                >OK</button>
                            </div>
                        </div>
                    )}

                    {warningMessage && (
                        <div className="popup-overlay">
                            <div className="popup-container">
                                <h3>Please Note</h3>
                                <p>{warningMessage}</p>
                                <button
                                    className="submit-popup-button"
                                    onClick={handleCloseWarning}
                                >OK</button>
                            </div>
                        </div>
                    )}
                </div>
            </main>
            <Footer />
        </div>
    );

};

export default Login;
